import React, { useEffect, useState } from 'react';
import { formatDate, formatDateTime } from '../utils/dateFmt';
import DateInput from './DateInput';
import Paginator, { usePaginator } from './Paginator';

/**
 * ScreeningAdmin — ky_thuat + ban_su_kien management for mam_non pass/fail screening.
 * Flow:
 *   1. Toggle "Đang mở sàng lọc". When open, every mam_non member with a
 *      pass/fail tick who hasn't acknowledged yet is forced to /letsmove.
 *   2. Tick each mam_non as pass / fail / (none = no decision).
 *   3. "Áp dụng" persists all pending changes.
 */
const ScreeningAdmin = ({ token, lang = {} }) => {
  const authHeaders = { Authorization: `Bearer ${token}` };
  const [screening, setScreening] = useState({ isOpen: false, openedByName: null, openedAt: null, totalDecisions: 0, slides: [] });
  const [members, setMembers] = useState([]);
  const membersPg = usePaginator(members, 10);
  const [pending, setPending] = useState({}); // { [userId]: 'pass' | 'fail' | null }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingSlides, setUploadingSlides] = useState(false);
  const [msg, setMsg] = useState(null);
  const slidesInputRef = React.useRef(null);
  const slidesAppendRef = React.useRef(null);
  const show = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg(null), 4000); };

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/screening/admin', { headers: authHeaders });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setScreening(data.screening);
      setMembers(data.members || []);
      setPending({});
    } catch (err) { show('error', err.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const toggleScreening = async () => {
    const nextState = !screening.isOpen;
    if (nextState && !window.confirm(
      lang.SCREENING_CONFIRM_OPEN || 'Bật chế độ thông báo kết quả training? Các thành viên mầm non đã được tick pass / trượt sẽ bị buộc chuyển tới /letsmove ở lần tương tác tiếp theo.'
    )) return;
    if (!nextState && !window.confirm(lang.SCREENING_CONFIRM_CLOSE || 'Tắt chế độ thông báo? Các quyết định đã lưu vẫn giữ nguyên.')) return;
    try {
      const res = await fetch('/api/screening/toggle', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ isOpen: nextState })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      show('success', nextState ? (lang.SCREENING_OPENED_TOAST || 'Đã bật thông báo.') : (lang.SCREENING_CLOSED_TOAST || 'Đã tắt thông báo.'));
      load();
    } catch (err) { show('error', err.message); }
  };

  const setLocal = (userId, result) => {
    setPending(prev => ({ ...prev, [userId]: result }));
  };

  // Effective result: pending override if any, otherwise current saved decision.
  const effective = (m) => {
    if (Object.prototype.hasOwnProperty.call(pending, m.id)) return pending[m.id];
    return m.decision?.result || null;
  };

  const dirtyCount = Object.keys(pending).filter(uid => {
    const m = members.find(x => x.id === parseInt(uid));
    const current = m?.decision?.result || null;
    return pending[uid] !== current;
  }).length;

  const apply = async () => {
    const payload = Object.entries(pending)
      .map(([uid, result]) => ({ userId: parseInt(uid), result }));
    if (payload.length === 0) return show('error', lang.SCREENING_NO_CHANGES || 'Chưa có thay đổi nào.');
    setSaving(true);
    try {
      const res = await fetch('/api/screening/decisions', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ decisions: payload })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      show('success', (lang.SCREENING_APPLIED || 'Đã cập nhật {n} quyết định.').replace('{n}', data.changed));
      load();
    } catch (err) { show('error', err.message); }
    finally { setSaving(false); }
  };

  const discard = () => setPending({});

  const uploadSlides = async (files, mode = 'replace') => {
    if (!files || files.length === 0) return;
    const existingCount = (screening.slides || []).length;
    if (mode === 'append') {
      if (existingCount + files.length > 6) {
        return show('error', `Tối đa 6 ảnh (đang có ${existingCount}, định thêm ${files.length}).`);
      }
    } else {
      if (files.length > 6) return show('error', lang.SCREENING_SLIDES_MAX || 'Tối đa 6 ảnh.');
    }
    if (files.length < 1) return show('error', lang.SCREENING_SLIDES_MIN || 'Cần tối thiểu 1 ảnh.');
    setUploadingSlides(true);
    try {
      const fd = new FormData();
      for (const f of files) fd.append('images', f);
      const res = await fetch(`/api/screening/slides?mode=${mode}`, { method: 'POST', headers: authHeaders, body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      show('success', (lang.SCREENING_SLIDES_UPDATED || 'Đã cập nhật {n} ảnh.').replace('{n}', data.slides.length));
      if (slidesInputRef.current) slidesInputRef.current.value = '';
      if (slidesAppendRef.current) slidesAppendRef.current.value = '';
      load();
    } catch (err) { show('error', err.message); }
    finally { setUploadingSlides(false); }
  };

  const deleteSlide = async (filename) => {
    if (!window.confirm(lang.SCREENING_SLIDE_DELETE_CONFIRM || 'Xoá ảnh này khỏi slideshow?')) return;
    try {
      const res = await fetch(`/api/screening/slides/${encodeURIComponent(filename)}`, {
        method: 'DELETE', headers: authHeaders
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      show('success', lang.SCREENING_SLIDE_DELETED || 'Đã xoá ảnh.');
      load();
    } catch (err) { show('error', err.message); }
  };

  const moveSlide = async (filename, dir) => {
    const arr = [...(screening.slides || [])];
    const i = arr.indexOf(filename);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    try {
      const res = await fetch('/api/screening/slides/order', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ order: arr })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setScreening(prev => ({ ...prev, slides: data.slides }));
    } catch (err) { show('error', err.message); }
  };

  const counts = members.reduce((acc, m) => {
    const r = effective(m);
    if (r === 'pass') acc.pass++;
    else if (r === 'fail') acc.fail++;
    else acc.none++;
    return acc;
  }, { pass: 0, fail: 0, none: 0 });

  // ----- Global auto-delete schedule for failed accounts -----
  // A single date (dd/mm/yyyy). At 00:00 Asia/Bangkok of that date, the
  // server sweeper deletes every account currently marked fail. Clearing
  // the field disables the schedule.
  const [autoDeleteDate, setAutoDeleteDate] = useState(''); // ISO 'YYYY-MM-DD'
  const [autoDeleteSaved, setAutoDeleteSaved] = useState(''); // ISO from server
  const [autoDeleteSaving, setAutoDeleteSaving] = useState(false);

  // Hydrate the global schedule once the screening payload arrives.
  useEffect(() => {
    const iso = screening.autoDeleteAt || '';
    if (iso) {
      // Stored as UTC ISO for 00:00 +07; compute the date part in +07.
      const d = new Date(iso);
      if (!isNaN(d.getTime())) {
        const shifted = new Date(d.getTime() + (7 * 60 + d.getTimezoneOffset()) * 60_000);
        const isoDate = shifted.toISOString().slice(0, 10);
        setAutoDeleteDate(isoDate);
        setAutoDeleteSaved(isoDate);
      }
    } else {
      setAutoDeleteDate('');
      setAutoDeleteSaved('');
    }
  }, [screening.autoDeleteAt]);

  const saveAutoDelete = async (clear = false) => {
    setAutoDeleteSaving(true);
    try {
      const body = clear
        ? { autoDeleteAt: null }
        : { autoDeleteAt: autoDeleteDate ? `${autoDeleteDate}T00:00:00+07:00` : null };
      const res = await fetch('/api/screening/auto-delete', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      show('success', clear
        ? (lang.SCREENING_AUTO_DEL_CLEARED || 'Đã huỷ lịch tự xoá.')
        : (lang.SCREENING_AUTO_DEL_SAVED || 'Đã đặt lịch tự xoá.'));
      load();
    } catch (err) {
      show('error', err.message);
    } finally {
      setAutoDeleteSaving(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontFamily: 'var(--font-display)' }}>{lang.SCREENING_TITLE || 'Thông Báo Kết Quả Training'}</h2>
          <p className="form-hint" style={{ margin: '4px 0 0', maxWidth: 560 }}>
            {lang.SCREENING_DESC || 'Khi mở chế độ thông báo, các thành viên mầm non đã được tick pass / trượt sẽ được chuyển tới trang /letsmove để nhận kết quả training. Sau khi họ xác nhận, quyền truy cập hệ thống sẽ trở lại bình thường (nếu pass) hoặc tự động đăng xuất (nếu trượt).'}
          </p>
        </div>
        <button
          className={`btn btn-sm ${screening.isOpen ? 'btn-danger' : 'btn-primary'}`}
          onClick={toggleScreening}
          style={{ flexShrink: 0 }}
        >
          {screening.isOpen ? (lang.SCREENING_TURN_OFF || '⏹ Tắt thông báo') : (lang.SCREENING_TURN_ON || '▶ Bật thông báo')}
        </button>
      </div>

      {msg && <div className={`training-msg ${msg.type}`} style={{ marginTop: 14 }}>{msg.text}</div>}

      <div className="screening-statusbar">
        <div className={`screening-status-pill ${screening.isOpen ? 'on' : 'off'}`}>
          {screening.isOpen ? (lang.SCREENING_STATUS_ON || '● Đang bật') : (lang.SCREENING_STATUS_OFF || '○ Đang tắt')}
        </div>
        {screening.isOpen && screening.openedByName && (
          <span className="form-hint">
            {lang.SCREENING_OPENED_BY || 'Bật bởi'} <strong>{screening.openedByName}</strong>
            {screening.openedAt && <> {lang.SCREENING_OPENED_AT || 'lúc'} {formatDateTime(screening.openedAt)}</>}
          </span>
        )}
        <span className="form-hint" style={{ marginLeft: 'auto' }}>
          {lang.SCREENING_COUNT_PASS || 'Pass'}: <strong style={{ color: '#22c55e' }}>{counts.pass}</strong>  ·  {lang.SCREENING_COUNT_FAIL || 'Trượt'}: <strong style={{ color: '#ef4444' }}>{counts.fail}</strong>  ·  {lang.SCREENING_COUNT_NONE || 'Chưa tick'}: <strong>{counts.none}</strong>
        </span>
      </div>

      {/* ============ Slideshow editor ============ */}
      <div className="screening-slideshow-panel">
        <div className="screening-slideshow-head">
          <div>
            <h3 style={{ margin: 0 }}>{lang.SCREENING_SLIDESHOW_TITLE || 'Ảnh Chia Tay'}</h3>
            <p className="form-hint" style={{ margin: '2px 0 0' }}>
              {lang.SCREENING_SLIDESHOW_HINT || 'Tối đa 6 ảnh (JPG/PNG, ≤15MB/ảnh). Slideshow sẽ hiển thị ở tất cả các slide thông báo trước quyết định cuối cùng.'}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input
              ref={slidesInputRef}
              type="file"
              accept="image/*"
              multiple
              style={{ display: 'none' }}
              onChange={(e) => { const files = Array.from(e.target.files || []); uploadSlides(files, 'replace'); }}
            />
            <input
              ref={slidesAppendRef}
              type="file"
              accept="image/*"
              multiple
              style={{ display: 'none' }}
              onChange={(e) => { const files = Array.from(e.target.files || []); uploadSlides(files, 'append'); }}
            />
            {Array.isArray(screening.slides) && screening.slides.length > 0 && screening.slides.length < 6 && (
              <button
                className="btn btn-outline btn-sm"
                disabled={uploadingSlides}
                onClick={() => slidesAppendRef.current?.click()}
              >
                ＋ Thêm ảnh ({screening.slides.length}/6)
              </button>
            )}
            <button
              className="btn btn-primary btn-sm"
              disabled={uploadingSlides}
              onClick={() => slidesInputRef.current?.click()}
            >
              {uploadingSlides
                ? (lang.SCREENING_SLIDES_UPLOADING || 'Đang upload...')
                : (Array.isArray(screening.slides) && screening.slides.length > 0
                    ? (lang.SCREENING_SLIDES_REPLACE || 'Thay toàn bộ ảnh')
                    : (lang.SCREENING_SLIDES_UPLOAD || 'Upload ảnh'))}
            </button>
          </div>
        </div>

        {Array.isArray(screening.slides) && screening.slides.length > 0 ? (
          <div className="screening-slide-grid">
            {screening.slides.map((fn, i) => (
              <div key={fn} className="screening-slide-item">
                <img src={`/uploads/screening/${fn}`} alt="" />
                <div className="screening-slide-overlay">
                  <span className="screening-slide-idx">{i + 1}</span>
                  <div className="screening-slide-buttons">
                    <button type="button" title={lang.SCREENING_SLIDE_MOVE_LEFT || 'Sang trái'} disabled={i === 0} onClick={() => moveSlide(fn, -1)}>←</button>
                    <button type="button" title={lang.SCREENING_SLIDE_MOVE_RIGHT || 'Sang phải'} disabled={i === screening.slides.length - 1} onClick={() => moveSlide(fn, 1)}>→</button>
                    <button type="button" title={lang.SCREENING_SLIDE_REMOVE || 'Xoá'} className="danger" onClick={() => deleteSlide(fn)}>✕</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="empty-text" style={{ marginTop: 10 }}>
            {lang.SCREENING_NO_SLIDES || 'Chưa có ảnh slideshow. Nếu để trống, trang /letsmove sẽ chỉ hiển thị phần text.'}
          </p>
        )}
      </div>

      {loading && <p className="empty-text">{lang.SCREENING_LOADING || 'Đang tải...'}</p>}
      {!loading && (
        <div className="screening-slideshow-panel" style={{ marginTop: 14 }}>
          <div className="screening-slideshow-head">
            <div>
              <h3 style={{ margin: 0 }}>{lang.SCREENING_AUTO_DEL_TITLE || 'Tự Xoá Tài Khoản Trượt'}</h3>
              <p className="form-hint" style={{ margin: '2px 0 0', maxWidth: 560 }}>
                {lang.SCREENING_AUTO_DEL_DESC || 'Đặt một ngày (dd/mm/yyyy). Vào lúc 00:00 (giờ Việt Nam) của ngày đó, hệ thống sẽ tự động xoá toàn bộ tài khoản hiện đang bị đánh dấu Trượt. Để trống để huỷ lịch.'}
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 10 }}>
            <DateInput
              value={autoDeleteDate}
              onChange={setAutoDeleteDate}
              placeholder={lang.COMMON_DATE_PH || 'dd/mm/yyyy'}
              style={{ minWidth: 160 }}
            />
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={autoDeleteSaving || autoDeleteDate === autoDeleteSaved || !autoDeleteDate}
              onClick={() => saveAutoDelete(false)}
            >
              {autoDeleteSaving ? '…' : (lang.SCREENING_AUTO_DEL_SAVE || '💾 Lưu lịch xoá')}
            </button>
            {autoDeleteSaved && (
              <button
                type="button"
                className="btn btn-danger btn-sm"
                disabled={autoDeleteSaving}
                onClick={() => saveAutoDelete(true)}
              >
                {lang.SCREENING_AUTO_DEL_CANCEL || '✕ Huỷ lịch'}
              </button>
            )}
            {autoDeleteSaved && (
              <span className="form-hint" style={{ marginLeft: 'auto' }}>
                {lang.SCREENING_AUTO_DEL_SCHEDULED_AT || 'Đã đặt lịch:'} <strong>{formatDate(autoDeleteSaved)}</strong>
                <span style={{ marginLeft: 6, color: 'var(--text-muted)' }}>(00:00 +07)</span>
              </span>
            )}
          </div>
        </div>
      )}

      {!loading && members.length === 0 && <p className="empty-text">{lang.SCREENING_NO_MAMNON || 'Không có thành viên nào đang ở bậc Mầm Non.'}</p>}

      {!loading && members.length > 0 && (
        <Paginator total={membersPg.total} page={membersPg.page} pageSize={membersPg.pageSize} onChange={membersPg.onChange} position="top" />
      )}

      {!loading && members.length > 0 && (
        <table className="training-problem-table" style={{ marginTop: 14 }}>
          <thead>
            <tr>
              <th>#</th>
              <th>{lang.SCREENING_TABLE_MEMBER || 'Thành viên'}</th>
              <th>{lang.SCREENING_TABLE_GEN || 'Gen'}</th>
              <th>{lang.SCREENING_TABLE_DECISION || 'Quyết định'}</th>
              <th>{lang.SCREENING_TABLE_SEEN || 'Đã xem'}</th>
            </tr>
          </thead>
          <tbody>
            {membersPg.paged.map((m, i) => {
              const eff = effective(m);
              const dirty = Object.prototype.hasOwnProperty.call(pending, m.id)
                && pending[m.id] !== (m.decision?.result || null);
              const realIdx = (membersPg.pageSize === 0 ? 0 : (membersPg.page - 1) * membersPg.pageSize) + i;
              return (
                <tr key={m.id} className={dirty ? 'is-dirty' : ''}>
                  <td>{realIdx + 1}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      {m.avatar
                        ? <img src={`/uploads/profiles/${m.avatar}`} alt="" style={{ width: 34, height: 34, borderRadius: '50%', objectFit: 'cover' }} />
                        : <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'var(--bg-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-mono)' }}>{(m.fullName || m.username || '?').charAt(0).toUpperCase()}</div>}
                      <div>
                        <div style={{ fontWeight: 600 }}>{m.fullName || m.username}</div>
                        <div className="training-muted" style={{ fontSize: '0.8rem' }}>{m.email}</div>
                      </div>
                    </div>
                  </td>
                  <td>{m.gen || <span className="training-muted">—</span>}</td>
                  <td>
                    <div className="screening-choice-group">
                      <button
                        type="button"
                        className={`screening-choice pass ${eff === 'pass' ? 'active' : ''}`}
                        onClick={() => setLocal(m.id, 'pass')}
                      >{lang.SCREENING_BTN_PASS || '✓ Pass'}</button>
                      <button
                        type="button"
                        className={`screening-choice fail ${eff === 'fail' ? 'active' : ''}`}
                        onClick={() => setLocal(m.id, 'fail')}
                      >{lang.SCREENING_BTN_FAIL || '✗ Trượt'}</button>
                      <button
                        type="button"
                        className={`screening-choice none ${!eff ? 'active' : ''}`}
                        onClick={() => setLocal(m.id, null)}
                      >{lang.SCREENING_BTN_NONE || '— Bỏ tick'}</button>
                    </div>
                  </td>
                  <td>
                    {m.decision?.acknowledgedAt
                      ? <span style={{ color: '#22c55e' }}>✓ {formatDateTime(m.decision.acknowledgedAt)}</span>
                      : m.decision
                        ? <span className="training-muted">{lang.SCREENING_NOT_SEEN || 'Chưa xem'}</span>
                        : <span className="training-muted">—</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {!loading && members.length > 0 && (
        <Paginator total={membersPg.total} page={membersPg.page} pageSize={membersPg.pageSize} onChange={membersPg.onChange} position="bottom" />
      )}

      <div style={{ marginTop: 16, display: 'flex', gap: 10, alignItems: 'center' }}>
        <button className="btn btn-primary btn-sm" onClick={apply} disabled={saving || dirtyCount === 0}>
          {saving ? (lang.SCREENING_SAVING || 'Đang lưu...') : `${lang.SCREENING_APPLY || 'Áp dụng'} (${dirtyCount})`}
        </button>
        <button className="btn btn-outline btn-sm" onClick={discard} disabled={dirtyCount === 0}>
          {lang.SCREENING_DISCARD || 'Huỷ thay đổi'}
        </button>
      </div>
    </div>
  );
};

export default ScreeningAdmin;
